import api from './api';

const attendanceService = {
  // Employee APIs
  checkIn: async (latitude, longitude, notes = '') => {
    try {
      const response = await api.post('/attendance/check-in', {
        latitude,
        longitude,
        notes
      });
      return response.data;
    } catch (error) {
      console.error('Check-in error:', error);
      throw error.response?.data || error;
    }
  },

  checkOut: async (latitude, longitude, notes = '') => {
    try {
      const response = await api.put('/attendance/check-out', {
        latitude,
        longitude,
        notes
      });
      return response.data;
    } catch (error) {
      console.error('Check-out error:', error);
      throw error.response?.data || error;
    }
  },

  getTodayAttendance: async () => {
    try {
      const response = await api.get('/attendance/today');
      return response.data;
    } catch (error) {
      console.error('Get today attendance error:', error);
      throw error.response?.data || error;
    }
  },

  // Admin: Get all employees' today attendance
  getAllAttendanceToday: async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await api.get('/attendance', {
        params: {
          startDate: today,
          endDate: today
        }
      });
      return response.data;
    } catch (error) {
      console.error('Get all attendance today error:', error);
      throw error.response?.data || error;
    }
  },

  getMyAttendance: async (startDate = null, endDate = null, limit = 30) => {
    try {
      const params = { limit };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get('/attendance/my-records', { params });
      return response.data;
    } catch (error) {
      console.error('Get my attendance error:', error);
      throw error.response?.data || error;
    }
  },

  getMyAttendanceSummary: async (month = null, year = null) => {
    try {
      const params = {};
      if (month) params.month = month;
      if (year) params.year = year;

      const response = await api.get('/attendance/my-summary', { params });
      return response.data;
    } catch (error) {
      console.error('Get my attendance summary error:', error);
      throw error.response?.data || error;
    }
  },

  // Manager/Admin APIs
  getUserAttendance: async (userId, startDate = null, endDate = null, limit = 30) => {
    try {
      const params = { limit };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get(`/attendance/user/${userId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Get user attendance error:', error);
      throw error.response?.data || error;
    }
  },

  getUserAttendanceStats: async (userId, month = null, year = null) => {
    try {
      const params = {};
      if (month) params.month = month;
      if (year) params.year = year;

      const response = await api.get(`/attendance/user/${userId}/stats`, { params });
      return response.data;
    } catch (error) {
      console.error('Get user attendance stats error:', error);
      throw error.response?.data || error;
    }
  },

  getUserAttendanceSummary: async (userId, month = null, year = null) => {
    try {
      const params = {};
      if (month) params.month = month;
      if (year) params.year = year;

      const response = await api.get(`/attendance/user/${userId}/summary`, { params });
      return response.data;
    } catch (error) {
      console.error('Get user attendance summary error:', error);
      throw error.response?.data || error;
    }
  },

  // Admin: Manually update attendance for a user
  updateAttendanceManually: async (userId, date, status) => {
    try {
      const response = await api.put(`/attendance/user/${userId}/manual`, {
        date,
        status
      });
      return response.data;
    } catch (error) {
      console.error('Update attendance manually error:', error);
      throw error.response?.data || error;
    }
  },

  getAllAttendance: async (filters = {}) => {
    try {
      const response = await api.get('/attendance', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Get all attendance error:', error);
      throw error.response?.data || error;
    }
  },

  // Get recent attendance with location data (last 7 days)
  getRecentAttendanceWithLocation: async (userId) => {
    try {
      const response = await api.get(`/attendance/recent/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Get recent attendance with location error:', error);
      throw error.response?.data || error;
    }
  },

  // Helper function to get user's current location
  getCurrentLocation: () => {
    return new Promise((resolve, reject) => {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      // Check if running on HTTPS (required for Safari and most modern browsers)
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        reject(new Error('Location services require HTTPS. Please access the site via HTTPS.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          let errorMessage = 'Unable to retrieve location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied.\n\n' +
                'Please enable location access:\n' +
                '• Desktop: Click the location icon in your browser\'s address bar\n' +
                '• Safari on iOS: Settings > Safari > Location > Allow\n' +
                '• Chrome on Android: Settings > Site Settings > Location > Allow';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please check your device settings.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
            default:
              errorMessage = 'An unknown error occurred while getting location.';
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 15000, // Increased from 10s to 15s for better Safari compatibility
          maximumAge: 0
        }
      );
    });
  }
};

export default attendanceService;
